import { ProvisionTenant, type PlatformTenant } from '@cyberscore/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../../lib/api';
import { useListState, usePage, type ListSpec, refreshLists } from '../../lib/list';
import { useSession, can } from '../../lib/session';
import { DataView, type Column } from '../../ui/DataView';
import { useToast } from '../../ui/Toasts';

const when = (iso: string) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }); // prettier-ignore

/** Platform administrators set up insurers and brokers (task 58). */
export function useOrganisationsAccess() {
  const { tenant } = useSession();
  return tenant?.kind === 'platform' && can(tenant, 'organisations.view');
}

const SPEC: ListSpec = { id: 'organisations', sort: 'created', view: 'cards', filters: ['kind'] };
const KIND = (k: PlatformTenant['kind']) => (k === 'insurer' ? 'Insurer' : 'Broker');

function AdminPill({ t }: { t: PlatformTenant }) {
  return t.admins ? <span className="pill ok">{t.admins} active</span> : t.open_invitations ? <span className="pill neutral">Invited</span> : <span className="pill bad">None: invite one</span>; // prettier-ignore
}

/**
 * Organisations (task 58): the insurers and brokers on the platform, and setting up a new one with its
 * first administrator, who is emailed an invitation and then invites everyone else. Cards or a table,
 * paged on the server (task 60).
 * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function OrganisationsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const list = useListState(SPEC);
  const page = usePage<PlatformTenant>('/platform/tenants', list.state);
  const columns: Column<PlatformTenant>[] = [
    {
      key: 'name',
      label: 'Organisation',
      sort: 'name',
      render: (t) => <span className="cell-title">{t.name}</span>,
    },
    { key: 'kind', label: 'Kind', render: (t) => KIND(t.kind) },
    {
      key: 'members',
      label: 'Members',
      sort: 'members',
      align: 'right',
      render: (t) => <span className="num">{t.members}</span>,
    },
    { key: 'admin', label: 'Administrator', render: (t) => <AdminPill t={t} /> },
    {
      key: 'created',
      label: 'Set up',
      sort: 'created',
      wide: true,
      render: (t) => <span className="muted">{when(t.created_at)}</span>,
    },
  ];
  return (
    <>
      <div className={`page-head${embedded ? ' embedded' : ''}`}>
        {embedded ? <h2>Organisations</h2> : <h1>Organisations</h1>}
        <p>
          Set up an insurer or broker and invite its first administrator by email. They join when they sign in or create an account with that address, then invite their own people. Businesses that come on their own set themselves up.
        </p>
      </div>
      <NewOrganisation />
      <h2 id="orgs-h" className="section-title mt">Insurers and brokers{page.data ? ` · ${page.data.total}` : ''}</h2>
      <DataView
        list={list}
        query={page}
        noun="organisations"
        rowKey={(t) => t.id}
        columns={columns}
        card={(t) => (
          <article className="ccard">
            <div className="ccard-head">
              <div className="minw"><h3>{t.name}</h3><div className="muted small">{KIND(t.kind)} · {t.country}</div></div>
              <AdminPill t={t} />
            </div>
            <div className="ccard-stats">
              <div><b>{t.members}</b><span>members</span></div>
              <div><b>{t.admins}</b><span>administrators</span></div>
              <div><b>{t.open_invitations}</b><span>invited</span></div>
            </div>
            <div className="ccard-foot"><span className="muted">Set up</span><span>{when(t.created_at)}</span></div>
          </article>
        )}
        filters={[{ key: 'kind', label: 'Kinds', options: [['insurer', 'Insurers'], ['broker', 'Brokers']] }]}
        sorts={[
          { value: 'created', label: 'Newest', dir: 'desc' },
          { value: 'name', label: 'Name', dir: 'asc' },
          { value: 'members', label: 'Members', dir: 'desc' },
        ]}
        searchLabel="Search organisations"
        empty={<><Building2 size={28} /><strong>No insurers or brokers yet</strong>Set one up above.</>}
      />
    </>
  ); // prettier-ignore
}

function NewOrganisation() {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'insurer' | 'broker'>('insurer');
  const [admin, setAdmin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (body: ProvisionTenant) => api<{ created: boolean }>('POST', '/platform/tenants', body),
    onSuccess: () => refreshLists(qc),
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = ProvisionTenant.safeParse({ name, kind, admin });
    if (!body.success) return setError(body.error.issues[0]!.message);
    setError(null);
    try {
      const r = await create.mutateAsync(body.data);
      toast(
        `${r.created ? 'Set up' : 'Found'} ${body.data.name} and emailed ${body.data.admin} an invitation.`,
        'ok',
      );
      setName('');
      setAdmin('');
    } catch (err) {
      setError(err instanceof ApiError ? (err.issues[0]?.message ?? err.message) : 'Something went wrong');
    }
  };
  return (
    <form className="card form-card" onSubmit={submit} noValidate>
      <div className="card-head">
        <div className="card-title row gap"><Building2 size={16} /> Set up an organisation</div>
      </div>
      <div className="form-body">
        <div className="field">
          <label htmlFor="org-name">Name</label>
          <input id="org-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pantai Takaful Berhad" maxLength={200} />
        </div>
        <div className="field">
          <label htmlFor="org-kind">Kind</label>
          <select id="org-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as 'insurer' | 'broker')}>
            <option value="insurer">Insurer</option>
            <option value="broker">Broker</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="org-admin">First administrator’s email</label>
          <input id="org-admin" className="input" type="email" autoComplete="off" value={admin} onChange={(e) => setAdmin(e.target.value)} placeholder="it.admin@company.com.my" />
          <span className="hint">We email them an invitation. If the organisation already exists, this invites another administrator.</span>
        </div>
        {error && <div className="error" role="alert">{error}</div>}
      </div>
      <div className="form-foot">
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>Set up and invite</button>
      </div>
    </form>
  ); // prettier-ignore
}
